import { useEffect, useMemo } from 'react';
import { useUserInfo } from '../../../../context/UserInfo';
import { useIsManagerMode } from '../../../../hooks/useIsManagerMode';
import {
  ManagerRole,
  ObjectiveMode,
  ObjectivePendingAction,
  ObjectivePhaseActionType,
  ObjectiveWeightMode,
} from '../../../../__generated__/graphql';
import { canRateGoal } from '../logic';
import {
  GoalVisibility,
  GoalType,
  allGoalStatuses,
  allVisibilityOptions,
} from '../types';
import {
  AutoGoalInfo,
  GoalSidebarMode,
  GoalStatusEx,
  GoalStatusWithRate,
  ObjectiveFormData,
} from './types';
import { useGoalsContext } from '../GoalsContext/GoalsContext';
import { useGoalFormContext } from './goalFormState';

type VisibleField =
  | 'employeeId'
  | 'teamId'
  | 'categoryId'
  | 'achievementScaleId'
  | 'objectiveResult'
  | 'weight'
  | 'keyResults'
  | 'parentObjectiveId'
  | 'sharedWithEmployeeIds'
  | 'statusWithRate'
  | 'manualProgress';

export type FieldVisibility = Record<VisibleField, boolean>;

export const useCommonFieldsLogic = (
  mode: GoalSidebarMode,
  ownerId: string,
) => {
  const { values, isSubmitting, setFieldValue } =
    useGoalFormContext<ObjectiveFormData>();
  const userInfo = useUserInfo();
  const { hasManagerRole } = useIsManagerMode();
  const {
    developmentFrameworkEnabled,
    objectiveMode,
    objectiveWeightMode,
    currentPeriodId,
    currentPeriod,
    isManagerView,
  } = useGoalsContext();

  const currentPhase = currentPeriod?.currentPhase;
  // A goal whose creation is still pending approval is semantically in "create"
  // territory: its phase-action gate is createGoals, not updateGoals.
  const isPendingCreation =
    values.pendingApprovalAction === ObjectivePendingAction.ObjCreate;
  const governingPhaseAction = isPendingCreation
    ? currentPhase?.createGoals
    : currentPhase?.updateGoals;
  // Managers bypass phase restrictions (server uses forceAllowAllActions)
  const isUpdateDenied =
    !isManagerView && governingPhaseAction === ObjectivePhaseActionType.Deny;

  const visibility = values.visibility as GoalVisibility;
  const timePeriodId = values.timePeriodId as string | null;
  const goalType = values.type as GoalType;
  const isManual = values.manualProgress;
  const goalOnTrackAutoState: undefined | boolean = values.goalOnTrackAutoState;

  const autoGoalInfo: AutoGoalInfo = {
    isAuto: !isManual,
    goalOnTrackAutoState,
  };

  /// Some Custom Logic during goal creation

  // when isManual is changed
  useEffect(() => {
    if (mode !== 'create') {
      return;
    }
    const curStatusWithRate = values.statusWithRate as
      | GoalStatusWithRate
      | undefined;
    if (!curStatusWithRate) {
      return;
    }
    if (isManual && curStatusWithRate.status === 'BEHIND_OR_ON_TRACK') {
      // we do not support this value in manual mode, so better drop
      setFieldValue('statusWithRate', {
        ...curStatusWithRate,
        status: 'BEHIND',
      });
    }
    if (
      !isManual &&
      (curStatusWithRate.status === 'BEHIND' ||
        curStatusWithRate.status === 'ON_TRACK')
    ) {
      // we do not support these values in auto mode, so better drop
      setFieldValue('statusWithRate', {
        ...curStatusWithRate,
        status: 'BEHIND_OR_ON_TRACK',
      });
    }
  }, [mode, isManual, setFieldValue, values.statusWithRate]);

  /// End of Some Custom Logic during goal creation

  // when developmentFrameworkEnabled is set we need to force set timePeriodId
  useEffect(() => {
    if (developmentFrameworkEnabled && currentPeriodId) {
      if (values.timePeriodId !== currentPeriodId) {
        setFieldValue('timePeriodId', currentPeriodId);
      }
    }
  }, [
    developmentFrameworkEnabled,
    currentPeriodId,
    values.timePeriodId,
    setFieldValue,
  ]);

  const availableStatuses = useMemo((): GoalStatusEx[] => {
    if (objectiveMode === ObjectiveMode.Mbo) {
      return [];
    }
    if (isManual) {
      return allGoalStatuses;
    }
    if (mode === 'create' || mode === 'update') {
      // all disabled
      return ['DRAFT', 'BEHIND_OR_ON_TRACK', 'DONE', 'CANCELED'];
    }
    return [];
  }, [mode, isManual, objectiveMode]);

  const readonly =
    mode === 'view' || isSubmitting || (mode === 'update' && isUpdateDenied);

  const isWeightReadonly =
    mode === 'view' ||
    isSubmitting ||
    (mode === 'update' &&
      !isManagerView &&
      currentPhase?.updateWeight === ObjectivePhaseActionType.Deny);

  const fieldVisibility = useMemo((): FieldVisibility => {
    const isTeamOrEmployee = goalType === 'team' || goalType === 'employee';
    const isMbo = objectiveMode === ObjectiveMode.Mbo;

    return {
      employeeId: goalType === 'employee',
      teamId: goalType === 'team',
      categoryId: developmentFrameworkEnabled,
      achievementScaleId: developmentFrameworkEnabled && isMbo,
      objectiveResult:
        isMbo &&
        goalType === 'employee' &&
        (mode === 'update' || mode === 'view'),
      weight:
        goalType === 'employee' &&
        objectiveWeightMode !== ObjectiveWeightMode.None,
      keyResults: !isMbo && isTeamOrEmployee,
      parentObjectiveId: isTeamOrEmployee,
      sharedWithEmployeeIds: visibility === 'protected',
      statusWithRate: !isMbo,
      manualProgress: !isMbo && isTeamOrEmployee,
    };
  }, [
    goalType,
    objectiveMode,
    objectiveWeightMode,
    developmentFrameworkEnabled,
    mode,
    visibility,
  ]);

  const isManualProgressEnabled = mode === 'create';

  const rateEnabled =
    objectiveMode === ObjectiveMode.Okr &&
    (mode === 'create' ||
      (mode === 'update' &&
        canRateGoal(
          {
            id: userInfo.id,
            isDirectManager: hasManagerRole(ManagerRole.DirectManager),
          },
          ownerId,
        )));

  const availableVisibilityOptions = useMemo((): GoalVisibility[] => {
    const isMbo = objectiveMode === ObjectiveMode.Mbo;
    if (isMbo) {
      return allVisibilityOptions.filter((v) => v !== 'protected');
    }
    return allVisibilityOptions;
  }, [objectiveMode]);

  return {
    readonly,
    availableStatuses,
    availableVisibilityOptions,
    fieldVisibility,
    isManualProgressEnabled,
    visibility,
    timePeriodId,
    goalType,
    rateEnabled,
    autoGoalInfo,
    isWeightReadonly,
  };
};
